import React, {useState} from 'react'
import {Box, Tab, Tabs, Typography} from '@mui/material'
import Card from 'src/components-new/layout/Card'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {
  formatDate,
  formatDeviceOfAssumption,
  formatFrequencyDays,
  formatModeOfAssumption,
  formatTherapyEach,
  formatTime,
  formatUnitOfMeasure
} from './therapyFormatters'

// Read-only mirror of src/components/templates/TherapyDialogs/TherapyCardTabs.tsx for a PRIMARY (main) therapy.
// Purely presentational — no patientId/CRUD dependency, so it can be reused both by the patient-detail
// "Terapie" tab and (in a later pass) by the standalone /therapies page.

interface PrimaryTherapyDetailProps {
  therapy: Record<string, any>
}

type SubTabKey = 'general' | 'frequency' | 'dosage' | 'administration'

function Field({label, value}: {label: string; value: React.ReactNode}): JSX.Element {
  return (
    <Box>
      <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.4}}>
        {label}
      </Typography>
      <Typography variant="body2">{value || value === 0 ? value : '—'}</Typography>
    </Box>
  )
}

function TwoColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
        gap: 2,
        mb: 1
      }}
    >
      {children}
    </Box>
  )
}

function ThreeColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
        gap: 2
      }}
    >
      {children}
    </Box>
  )
}

function CustomDosagesBlock({dosages}: {dosages: Record<string, any>[]}): JSX.Element {
  return (
    <Box sx={{mt: 2}}>
      <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 1, textTransform: 'uppercase', letterSpacing: 0.4}}>
        Dosaggio personalizzato
      </Typography>
      <ThreeColGrid>
        {dosages.map((dosage, index) => (
          <Card key={index} title={`Assunzione ${index + 1}`}>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              <Field label="Dosaggio" value={formatUnitOfMeasure(dosage)} />
              {dosage.infusionRate && <Field label="Velocità di infusione" value={formatUnitOfMeasure(dosage.infusionRate)} />}
              {dosage.secondaryDosage && <Field label="Dosaggio secondario" value={formatUnitOfMeasure(dosage.secondaryDosage)} />}
              {dosage.dilution && <Field label="Diluizione (ml)" value={dosage.dilution} />}
              {dosage.calculationCriterion && <Field label="Criterio di calcolo" value={formatUnitOfMeasure(dosage.calculationCriterion)} />}
            </Box>
          </Card>
        ))}
      </ThreeColGrid>
    </Box>
  )
}

export default function PrimaryTherapyDetail({therapy}: PrimaryTherapyDetailProps): JSX.Element {
  const {
    assumptionDosage = {},
    calculationCriterion = {},
    customDosages,
    description = '',
    deviceOfAssumption = '',
    drugDosage = {},
    endDate,
    reminderEndDate,
    frequency = {},
    isContinued,
    modeOfAssumption = '',
    monthlyDosage = {},
    dailyDosage = {},
    vialDosage = {},
    notes = '',
    startDate,
    assumptionsStartDate,
    weeklyDosage = {},
    speedOfDevice = {},
    diagnosisDate,
    treatmentStartDate,
    homeTreatmentStartDate,
    preTreatment,
    preTreatmentNotes,
    postTreatment,
    postTreatmentNotes,
    emergencyTreatment,
    emergencyTreatmentNotes
  } = therapy

  const subTabs: {key: SubTabKey; label: string}[] = [
    {key: 'general', label: 'Generali'},
    ...(!isContinued ? [{key: 'frequency' as SubTabKey, label: 'Frequenza'}] : []),
    {key: 'dosage', label: 'Dosaggio'},
    {key: 'administration', label: 'Modalità di assunzione'}
  ]

  const [activeSubTab, setActiveSubTab] = useState<SubTabKey>('general')
  const currentTab = subTabs.some(t => t.key === activeSubTab) ? activeSubTab : subTabs[0].key

  return (
    <Box>
      <Tabs
        value={currentTab}
        onChange={(_e, val: SubTabKey) => setActiveSubTab(val)}
        variant="scrollable"
        scrollButtons="auto"
        // Without this MUI hides the arrows on touch devices and expects a swipe instead — not
        // discoverable inside a card this narrow, so the arrows are forced on for mobile too.
        allowScrollButtonsMobile
        sx={{
          mb: 2,
          minHeight: 40,
          '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
          '& .MuiTabs-indicator': {height: 2}
        }}
      >
        {subTabs.map(tab => <Tab key={tab.key} value={tab.key} label={tab.label} />)}
      </Tabs>

      {currentTab === 'general' && (
        <Card>
          <TwoColGrid>
            <Field label="Data di inizio terapia" value={formatDate(startDate, 'DD MMMM YYYY')} />
            <Field label="Data di inizio assunzioni" value={formatDate(assumptionsStartDate, 'DD MMMM YYYY')} />
            <Field label="Data promemoria scadenza" value={formatDate(reminderEndDate, 'DD MMMM YYYY')} />
            <Field label="Data diagnosi" value={formatDate(diagnosisDate, 'DD MMMM YYYY')} />
            <Field label="Data inizio trattamento" value={formatDate(treatmentStartDate, 'DD MMMM YYYY')} />
            <Field label="Data inizio trattamento domiciliare" value={formatDate(homeTreatmentStartDate, 'DD MMMM YYYY')} />
            <Field label="Data di fine" value={formatDate(endDate, 'DD MMMM YYYY')} />
            <Field label="Terapia continuativa" value={isContinued ? 'Sì' : 'No'} />
            <Field label="Descrizione" value={description} />
            <Field label="Pre-trattamento" value={preTreatment ? (preTreatmentNotes ?? 'Sì') : 'No'} />
            <Field label="Post-trattamento" value={postTreatment ? (postTreatmentNotes ?? 'Sì') : 'No'} />
            <Field label="Trattamento di emergenza" value={emergencyTreatment ? (emergencyTreatmentNotes ?? 'Sì') : 'No'} />
            <Field label="Note" value={notes} />
          </TwoColGrid>
        </Card>
      )}

      {currentTab === 'frequency' && !isContinued && (
        <Card>
          <TwoColGrid>
            <Field label="Frequenza assunzioni" value={`${frequency.times} ogni ${formatTherapyEach(frequency.each)}`} />
            {Boolean(frequency.days?.length) && (
              <Field label="Da ripetere nei giorni" value={formatFrequencyDays(frequency.days)} />
            )}
            <Field label="Orario prima assunzione" value={formatTime(frequency.notificationTime)} />
            <Field label="Giorni di tolleranza" value={formatUnitOfMeasure(frequency.toleranceDays)} />
          </TwoColGrid>
        </Card>
      )}

      {currentTab === 'dosage' && (
        <Card>
          <TwoColGrid>
            {!customDosages && (
              <>
                <Field label="Dosaggio per assunzione" value={formatUnitOfMeasure(assumptionDosage)} />
                <Field label="Velocità infusionale" value={formatUnitOfMeasure(assumptionDosage.infusionRate)} />
                <Field label="Dosaggio secondario" value={formatUnitOfMeasure(assumptionDosage.secondaryDosage)} />
                <Field label="Diluizione (ml)" value={assumptionDosage.dilution} />
              </>
            )}
            <Field label="Dosaggio singolo" value={formatUnitOfMeasure(drugDosage)} />
            <Field label="Dosaggio settimanale" value={formatUnitOfMeasure(weeklyDosage)} />
            <Field label="Dosaggio mensile" value={formatUnitOfMeasure(monthlyDosage)} />
            <Field label="Dosaggio giornaliero" value={formatUnitOfMeasure(dailyDosage)} />
            <Field label="Dosaggio fiala" value={formatUnitOfMeasure(vialDosage)} />
            <Field label="Criterio di calcolo" value={formatUnitOfMeasure(calculationCriterion)} />
          </TwoColGrid>
          {customDosages && <CustomDosagesBlock dosages={customDosages} />}
        </Card>
      )}

      {currentTab === 'administration' && (
        <Card>
          <TwoColGrid>
            <Field label="Via di assunzione" value={formatModeOfAssumption(modeOfAssumption)} />
            <Field label="Dispositivo" value={formatDeviceOfAssumption(deviceOfAssumption)} />
            <Field label="Velocità device" value={formatUnitOfMeasure(speedOfDevice)} />
          </TwoColGrid>
        </Card>
      )}
    </Box>
  )
}
